import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from "recharts";
import { History, TrendingUp, TrendingDown, Minus, ChevronRight, Trash2 } from "lucide-react";

import { API } from "../lib/api";

function getRiskColor(level) {
  if (!level) return { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" };
  const l = level.toLowerCase();
  if (l.includes("healthy")) return { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" };
  if (l.includes("moderate")) return { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" };
  if (l.includes("high")) return { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500" };
  return { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" };
}

function getScoreColor(score) {
  if (score >= 80) return "#10B981";
  if (score >= 60) return "#F59E0B";
  if (score >= 40) return "#F97316";
  return "#EF4444";
}

export default function HistoryPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/api/projects`, { withCredentials: true })
      .then(({ data }) => setProjects(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const deleteProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project?")) return;
    await axios.delete(`${API}/api/projects/${id}`, { withCredentials: true });
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };

  const chartData = [...projects].reverse().map((p, i) => ({
    name: p.name.length > 12 ? p.name.slice(0, 12) + "…" : p.name,
    score: p.piq_score,
    date: new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    idx: i + 1
  }));

  const avgScore = projects.length
    ? Math.round(projects.reduce((s, p) => s + p.piq_score, 0) / projects.length)
    : 0;

  const trend = projects.length >= 2
    ? projects[0].piq_score - projects[projects.length - 1].piq_score
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-slide-up">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <History className="w-5 h-5 text-slate-700" />
              <h1 className="font-outfit text-3xl font-black text-slate-900">Project History</h1>
            </div>
            <p className="font-ibm-plex text-sm text-slate-500">
              {projects.length} project{projects.length !== 1 ? "s" : ""} analyzed
            </p>
          </div>
          <button
            onClick={() => navigate("/project/new")}
            className="bg-slate-900 text-white font-outfit font-semibold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-colors text-sm"
          >
            New Analysis
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-16 text-center">
            <History className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2">No history yet</h3>
            <p className="font-ibm-plex text-slate-500 text-sm mb-6">Start analyzing projects to build your history.</p>
            <button onClick={() => navigate("/project/new")}
              className="bg-slate-900 text-white font-outfit font-semibold px-6 py-3 rounded-lg hover:bg-slate-800">
              Create First Analysis
            </button>
          </div>
        ) : (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 mb-6 animate-slide-up stagger-1">
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Avg Score</p>
                <p className="font-outfit text-3xl font-black" style={{ color: getScoreColor(avgScore) }}>{avgScore}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Latest</p>
                <p className="font-outfit text-3xl font-black" style={{ color: getScoreColor(projects[0]?.piq_score) }}>
                  {projects[0]?.piq_score || "–"}
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Trend</p>
                <div className="flex items-center gap-2">
                  {trend > 0 ? (
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  ) : trend < 0 ? (
                    <TrendingDown className="w-5 h-5 text-red-500" />
                  ) : (
                    <Minus className="w-5 h-5 text-slate-400" />
                  )}
                  <span className={`font-outfit text-2xl font-black ${trend > 0 ? "text-emerald-600" : trend < 0 ? "text-red-600" : "text-slate-400"}`}>
                    {trend > 0 ? `+${trend.toFixed(1)}` : trend.toFixed(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Trend Chart */}
            {chartData.length >= 2 && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 animate-slide-up stagger-2">
                <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">PIQ Score Over Time</h2>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="date" tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#9CA3AF" }} />
                    <YAxis domain={[0, 100]} tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#9CA3AF" }} />
                    <Tooltip
                      contentStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12, border: "1px solid #E5E7EB" }}
                      formatter={(v) => [`${v}/100`, "PIQ Score"]}
                    />
                    <ReferenceLine y={80} stroke="#10B981" strokeDasharray="4 4" strokeOpacity={0.5} />
                    <ReferenceLine y={60} stroke="#F59E0B" strokeDasharray="4 4" strokeOpacity={0.5} />
                    <ReferenceLine y={40} stroke="#EF4444" strokeDasharray="4 4" strokeOpacity={0.5} />
                    <Line type="monotone" dataKey="score" stroke="#0F172A" strokeWidth={2}
                      dot={{ fill: "#0F172A", r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-6 mt-2 justify-end">
                  {[{ c: "#10B981", l: "Healthy (≥80)" }, { c: "#F59E0B", l: "Moderate (≥60)" }, { c: "#EF4444", l: "Critical (<40)" }].map((r) => (
                    <div key={r.l} className="flex items-center gap-1.5">
                      <div className="w-4 h-px border-t border-dashed" style={{ borderColor: r.c }} />
                      <span className="font-ibm-plex text-xs text-slate-400">{r.l}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Projects Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden animate-slide-up stagger-3">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {["Project", "Type", "PIQ Score", "Risk Level", "Failure %", "Date", ""].map((h) => (
                      <th key={h} className="text-left px-4 py-3 font-ibm-plex text-xs font-semibold text-slate-500 uppercase tracking-widest">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, i) => {
                    const rc = getRiskColor(p.risk_level);
                    return (
                      <tr
                        key={p.id}
                        data-testid={`history-row-${p.id}`}
                        onClick={() => navigate(`/project/${p.id}`)}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                        style={{ animationDelay: `${i * 0.04}s` }}
                      >
                        <td className="px-4 py-3">
                          <span className="font-outfit font-semibold text-slate-900 text-sm">{p.name}</span>
                          {p.description && (
                            <p className="font-ibm-plex text-xs text-slate-400 truncate max-w-[200px]">{p.description}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 font-ibm-plex text-sm text-slate-500">{p.project_type}</td>
                        <td className="px-4 py-3">
                          <span className="font-outfit font-black text-base" style={{ color: getScoreColor(p.piq_score) }}>
                            {p.piq_score}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-ibm-plex font-medium ${rc.bg} ${rc.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${rc.dot}`} />
                            {p.risk_level}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-ibm-plex text-sm text-slate-700">{p.failure_probability}%</td>
                        <td className="px-4 py-3 font-ibm-plex text-xs text-slate-400">
                          {new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              data-testid={`delete-history-${p.id}`}
                              onClick={(e) => deleteProject(p.id, e)}
                              className="p-1.5 text-slate-300 hover:text-red-500 rounded transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <ChevronRight className="w-4 h-4 text-slate-300" />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
